import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';

import { buildAdminIntakeHubPath } from '../adminJourneyRouting';
import type { IntakeVerificationPractice, IntakeVerificationQueueResponse } from '../types';

import { IntakePill } from './shell/IntakePill';
import { footnoteSx, PILL_TONES, tableHeadSx, wf } from './shell/tokens';

export const VERIFICATION_FILTERS = [
  { value: 'awaiting', label: 'Awaiting verification' },
  { value: 'all', label: 'All' },
  { value: 'verified', label: 'Verified' },
  { value: 'incomplete', label: 'Incomplete' },
] as const;

export type VerificationFilter = (typeof VERIFICATION_FILTERS)[number]['value'];

const TILE_HINTS: Record<VerificationFilter, string> = {
  awaiting: 'Intake submitted, not yet reviewed',
  all: 'Eligible practices in this search',
  verified: 'Every reviewed provider verified',
  incomplete: 'Waiting on the practice',
};

const TILE_COLORS: Record<VerificationFilter, string> = {
  awaiting: wf.amber,
  all: wf.ink,
  verified: wf.green,
  incomplete: wf.slate,
};

const cardSx = {
  border: `1px solid ${wf.line}`,
  borderRadius: '10px',
  overflow: 'hidden',
  backgroundColor: wf.surface,
};
const providerGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1.4fr) 140px 190px' },
  gap: '14px',
  alignItems: 'center',
};
const metaSx = { fontSize: '12px', color: wf.meta, mt: '2px' };

function dateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleDateString();
}

type ReviewState = IntakeVerificationPractice['verification']['state'];

const stateLabel = (state: ReviewState) =>
  state === 'verified' ? 'Verified' : state === 'awaiting' ? 'Awaiting verification' : 'Incomplete';

const stateTone = (state: ReviewState) =>
  state === 'verified' ? 'green' : state === 'awaiting' ? 'amber' : 'slate';

export function IntakeVerificationQueue({
  data,
  returnTo,
  loading,
  failed,
  search,
  filter,
  page,
  perPage,
  onSearch,
  onFilter,
  onPage,
  onPerPage,
  onRefresh,
}: {
  data?: IntakeVerificationQueueResponse;
  returnTo?: string;
  loading: boolean;
  failed: boolean;
  search: string;
  filter: VerificationFilter;
  page: number;
  perPage: number;
  onSearch: (value: string) => void;
  onFilter: (value: VerificationFilter) => void;
  onPage: (value: number) => void;
  onPerPage: (value: number) => void;
  onRefresh: () => void;
}): React.JSX.Element {
  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Search practice name"
          slotProps={{ htmlInput: { maxLength: 200 } }}
          value={search}
          onChange={event => onSearch(event.target.value)}
          size="small"
          fullWidth
          sx={{ backgroundColor: wf.surface }}
        />
        <TextField
          select
          label="Review status"
          value={filter}
          onChange={event => {
            const option = VERIFICATION_FILTERS.find(item => item.value === event.target.value);
            if (option) onFilter(option.value);
          }}
          size="small"
          sx={{ minWidth: 220, backgroundColor: wf.surface }}
        >
          {VERIFICATION_FILTERS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={onRefresh} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      {failed ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={onRefresh}>
              Retry
            </Button>
          }
        >
          Could not load the verification queue. Refresh to try again.
        </Alert>
      ) : (
        <>
          <Box aria-live="polite">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(158px, 1fr))',
                gap: '12px',
              }}
            >
              {VERIFICATION_FILTERS.map(option => {
                const active = filter === option.value;
                return (
                  <ButtonBase
                    key={option.value}
                    onClick={() => onFilter(option.value)}
                    aria-pressed={active}
                    sx={{
                      ...cardSx,
                      display: 'block',
                      textAlign: 'left',
                      padding: '14px 16px',
                      borderColor: active ? wf.blue : wf.line,
                      boxShadow: active ? `0 0 0 1px ${wf.blue} inset` : 'none',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '24px',
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                        color: TILE_COLORS[option.value],
                      }}
                    >
                      {data
                        ? data.practice_counts[option.value === 'all' ? 'total' : option.value]
                        : '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: wf.ink }}>
                      {option.value === 'all' ? 'All practices' : option.label}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: wf.meta, lineHeight: 1.45 }}>
                      {TILE_HINTS[option.value]}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Box>
            <Typography sx={{ ...footnoteSx, mt: '10px' }}>
              Practice counts for your search, across every result page. Selecting a tile filters
              the queue below.
            </Typography>
          </Box>
          {loading && !data ? (
            <Typography sx={{ fontSize: '13px', color: wf.muted }}>
              Loading the verification queue…
            </Typography>
          ) : (data?.items.length ?? 0) === 0 ? (
            <Box sx={{ ...cardSx, p: 4, textAlign: 'center' }}>
              <Typography>No practices match these filters.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.75}>
              {(data?.items ?? []).map(row => (
                <PracticeCard key={row.organization_id} row={row} returnTo={returnTo} />
              ))}
            </Stack>
          )}
          <Typography sx={footnoteSx}>
            Same verify action as today, one provider at a time, in that provider’s intake review.
            Providers with an incomplete intake have nothing to verify yet.
          </Typography>
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page - 1}
            rowsPerPage={perPage}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={(_event, next) => onPage(next + 1)}
            onRowsPerPageChange={event => onPerPage(Number(event.target.value))}
            labelRowsPerPage="Practices per page"
          />
        </>
      )}
    </Stack>
  );
}

function PracticeCard({
  row,
  returnTo,
}: {
  row: IntakeVerificationPractice;
  returnTo?: string;
}): React.JSX.Element {
  const counts = row.verification.provider_counts;
  return (
    <Box sx={cardSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '14px 20px',
          borderBottom: `1px solid ${wf.lineSoft}`,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: wf.ink }}>
              {row.organization_name}
            </Typography>
            <IntakePill
              tone={stateTone(row.verification.state)}
              label={stateLabel(row.verification.state)}
            />
          </Stack>
          <Typography sx={metaSx}>
            {counts.verified} verified · {counts.awaiting} awaiting · {counts.incomplete} incomplete
          </Typography>
          <Typography sx={metaSx}>
            Oldest outstanding submission {dateLabel(row.verification.submitted_at)}
          </Typography>
          {row.providers.length < counts.total && (
            <Typography sx={metaSx}>Legacy provider intakes are excluded from review.</Typography>
          )}
        </Box>
        <Button
          component={Link}
          to={buildAdminIntakeHubPath({
            organizationId: row.organization_id,
            stage: 'verification',
            returnTo,
          })}
          variant="outlined"
          size="small"
          aria-label={`Review intake for ${row.organization_name}`}
          sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 700 }}
        >
          Review intake
        </Button>
      </Box>
      <Box
        sx={{
          ...providerGrid,
          ...tableHeadSx,
          padding: '8px 20px',
          display: { xs: 'none', sm: 'grid' },
        }}
      >
        <Box>Provider</Box>
        <Box>Intake</Box>
        <Box>Verification</Box>
      </Box>
      {row.providers.map(provider => {
        const incomplete = provider.state === 'incomplete' || provider.journeys_pending;
        const tone = stateTone(provider.state);
        return (
          <Box
            key={provider.user_id}
            sx={{ ...providerGrid, padding: '12px 20px', borderBottom: `1px solid #f3f3f7` }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: wf.ink }}>
                {provider.display_name || 'Provider name unavailable'}
              </Typography>
              <Typography sx={metaSx}>
                {provider.journeys_pending
                  ? 'Enrollment pending'
                  : provider.verified_at
                    ? `Checked ${dateLabel(provider.verified_at)}`
                    : provider.submitted_at
                      ? `Submitted ${dateLabel(provider.submitted_at)}`
                      : 'Not submitted'}
              </Typography>
            </Box>
            <Box>
              <IntakePill
                tone={incomplete ? 'slate' : 'green'}
                label={incomplete ? 'Incomplete' : 'Complete'}
              />
            </Box>
            <Box>
              <Button
                component={Link}
                size="small"
                variant="outlined"
                fullWidth
                to={buildAdminIntakeHubPath({
                  organizationId: row.organization_id,
                  stage: 'verification',
                  providerScope: provider.user_id,
                  returnTo,
                })}
                sx={{
                  justifyContent: 'flex-start',
                  fontSize: '12px',
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  color: PILL_TONES[tone].fg,
                  backgroundColor: PILL_TONES[tone].bg,
                  borderColor: PILL_TONES[tone].bd,
                  '&:hover': { borderColor: PILL_TONES[tone].fg },
                }}
              >
                Review provider
              </Button>
              <Typography sx={{ ...metaSx, fontSize: '11px' }}>
                {stateLabel(provider.state)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
